import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import type { AppShell } from "@/App"
import { downloadDocument, errorMessage, getPlan, listOrders, listVehicles, listWarehouses, type Order, type PlanView, type Vehicle, type Warehouse } from "@/lib/api"
import { day10, dayShort, eur, kg, m3, n, todayLisbon } from "@/lib/format"
import { AsyncButton, ErrorBox, Loading, Note, Page, Panel, Tag, Td, Th } from "@/components/kit"
import { DeadlineCell, isOpen, LoadCell } from "@/pages/Orders"
import { cn } from "@/lib/utils"
import { FleetBoard } from "@/pages/FleetBoard"
import { MoveDialog } from "@/pages/MoveDialog"
import { Button } from "@/components/ui/button"
import { ArrowRightLeft, FileSpreadsheet } from "lucide-react"

/**
 * Planeamento: exceções no topo, KPIs, «Por resolver», a proposta da frota
 * (quadro dias × veículos, F1) e as encomendas que ficam para transportadora.
 */
export function Planning({ shell }: { shell: AppShell }) {
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [plan, setPlan] = useState<PlanView | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [moving, setMoving] = useState<Order | null>(null)
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const today = todayLisbon()

  const load = useCallback(async () => {
    try {
      const [o, p, v, w] = await Promise.all([listOrders(shell), getPlan(shell), listVehicles(shell), listWarehouses(shell)])
      setWarehouse(w.find((x) => x.active !== false) ?? null)
      setOrders(o)
      setPlan(p)
      setVehicles(v)
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [shell])
  useEffect(() => void load(), [load])
  const reloadPlan = useCallback(async () => setPlan(await getPlan(shell)), [shell])

  const view = useMemo(() => {
    const open = (orders ?? []).filter(isOpen)
    const dueToday = open.filter((o) => day10(o.due_date) === today)
    const late = open.filter((o) => { const d = day10(o.due_date); return !!d && d < today })
    const blocked = open.filter((o) => o.status === "blocked")
    const lastCarrierToday = open.filter((o) => o.status !== "blocked" && day10(o.carrier_deadline) === today)
    const noBackup = open.filter((o) => o.status !== "blocked" && !o.carrier_deadline)
    const ok = open.filter((o) => o.status !== "blocked")
    // Com proposta, só as que ela manda para a transportadora (mais as de fora da área).
    const toCarrier = plan?.plan ? new Set(plan.plan.carrier) : null
    const byCarrier = new Map<string, { id: string; name: string; orders: Order[]; cost: number }>()
    let allCarrierCost = 0
    for (const o of ok) {
      allCarrierCost += o.options.find((x) => x.ok && x.service_id === o.carrier_service_id)?.cost ?? 0
      if (toCarrier && !toCarrier.has(o.id) && o.delivery_kind !== "carrier") continue
      const best = o.options.find((x) => x.ok && x.service_id === o.carrier_service_id)
      if (!best) continue
      const g = byCarrier.get(best.carrier_id) ?? { id: best.carrier_id, name: best.carrier_name, orders: [], cost: 0 }
      g.orders.push(o)
      g.cost += best.cost ?? 0
      byCarrier.set(best.carrier_id, g)
    }
    return {
      open,
      dueToday,
      late,
      blocked,
      lastCarrierToday,
      noBackup,
      kg: ok.reduce((a, o) => a + (n(o.total_kg) ?? 0), 0),
      m3: ok.reduce((a, o) => a + (n(o.total_m3) ?? 0), 0),
      lift: ok.filter((o) => o.needs_tail_lift).length,
      onlyCarrier: ok.filter((o) => o.delivery_kind === "carrier").length,
      carrierCost: allCarrierCost,
      byCarrier: [...byCarrier.values()].sort((a, b) => b.orders.length - a.orders.length),
    }
  }, [orders, plan, today])

  if (error) return <Page><ErrorBox error={error} /></Page>
  if (!orders) return <Loading />

  const Exc = ({ n: count, title, sub, tone, to }: { n: number; title: string; sub: string; tone: "bad" | "warn" | "calm"; to: string }) => (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 rounded-xl border bg-card p-3 hover:border-foreground/30",
        tone === "bad" && "border-red-300 bg-red-50",
        tone === "warn" && "border-amber-300 bg-amber-50",
      )}
    >
      <span className={cn("min-w-[2ch] font-mono text-2xl", tone === "bad" ? "text-red-700" : tone === "warn" ? "text-amber-700" : "text-green-700")}>{count}</span>
      <span className="flex flex-col">
        <b className="text-sm">{title}</b>
        <span className="text-xs text-muted-foreground">{sub}</span>
      </span>
    </Link>
  )

  return (
    <Page>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Exc
          n={view.dueToday.length}
          title="Vencem hoje"
          sub={view.lastCarrierToday.length ? `${view.lastCarrierToday.length} com a última hipótese de transportadora hoje` : "prazo de entrega hoje"}
          tone={view.dueToday.length ? "bad" : view.lastCarrierToday.length ? "warn" : "calm"}
          to="/encomendas?f=today"
        />
        <Exc n={view.late.length} title="Em atraso" sub={view.late.length ? "prazo já passou" : "nenhuma"} tone={view.late.length ? "warn" : "calm"} to="/encomendas?f=late" />
        <Exc n={view.blocked.length} title="Por resolver" sub={view.blocked.length ? "sem localização ou sem opção no prazo · ver nas Encomendas" : "nenhuma"} tone={view.blocked.length ? "bad" : "calm"} to="/encomendas?f=blocked" />
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 px-1 text-[12.5px] text-muted-foreground">
        <span><b className="font-mono text-foreground">{view.open.length}</b> encomendas em aberto</span>
        <span><b className="font-mono text-foreground">{kg(view.kg)}</b> · <b className="font-mono text-foreground">{m3(view.m3)}</b></span>
        <span><b className="font-mono text-foreground">{view.lift}</b> pedem plataforma</span>
        <span><b className="font-mono text-foreground">{view.onlyCarrier}</b> só por transportadora (fora da área da frota)</span>
        <span><b className="font-mono text-foreground">{view.noBackup.length}</b> sem transportadora de reserva</span>
        <span title="Soma da melhor transportadora no prazo de cada encomenda: o que a frota tem de bater">Tudo por transportadora: <b className="font-mono text-foreground">{eur(view.carrierCost)}</b></span>
        <span><b className="font-mono text-foreground">{view.lastCarrierToday.length}</b> com a última hipótese de transportadora hoje</span>
      </div>

      {plan && <FleetBoard shell={shell} view={plan} vehicles={vehicles} warehouse={warehouse} orders={orders} onChanged={setPlan} onReload={reloadPlan} />}

      <Panel
        title="Pela transportadora mais barata no prazo"
        subtitle={plan?.plan ? "As que a proposta manda para a transportadora (e as de fora da área da frota), por transportadora." : "O custo que a frota própria tem de bater, por transportadora."}
        actions={plan?.plan && view.byCarrier.length > 1 ? (
          <AsyncButton size="sm" variant="outline" onRun={() => downloadDocument(shell, "shipping_list").catch((err) => shell.toast("error", "Lista de envios", errorMessage(err)))}>
            <FileSpreadsheet className="h-3.5 w-3.5" /> Lista de envios (todas)
          </AsyncButton>
        ) : undefined}
      >
        {view.byCarrier.length === 0 && <div className="text-sm text-muted-foreground">Sem encomendas com transportadora no prazo.</div>}
        <div className="flex flex-col gap-4">
          {view.byCarrier.map((g) => (
            <div key={g.name}>
              <div className="mb-1 flex items-center justify-between gap-2">
                <h3 className="text-[13px] font-semibold">{g.name} · {g.orders.length} encomenda(s) · {eur(g.cost)}</h3>
                {plan?.plan && (
                  <AsyncButton size="sm" variant="outline" className="h-7" onRun={() => downloadDocument(shell, "shipping_list", { carrier_id: g.id }).catch((err) => shell.toast("error", "Lista de envios", errorMessage(err)))}>
                    <FileSpreadsheet className="h-3.5 w-3.5" /> Lista de envios
                  </AsyncButton>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead><tr><Th>Encomenda</Th><Th>Cliente</Th><Th>Localidade</Th><Th>Carga</Th><Th>Custo</Th><Th>Chegada</Th><Th>Prazo</Th><Th>Frota?</Th></tr></thead>
                  <tbody>
                    {g.orders.map((o) => {
                      const best = o.options.find((x) => x.service_id === o.carrier_service_id)
                      return (
                        <tr key={o.id}>
                          <Td className="font-mono text-xs">{o.source_ref}</Td>
                          <Td>{o.customer_name ?? "—"}</Td>
                          <Td>{o.locality ?? "—"}</Td>
                          <Td><LoadCell o={o} /></Td>
                          <Td className="font-mono text-xs">{eur(best?.cost)}</Td>
                          <Td className="font-mono text-xs">{dayShort(best?.arrival)}</Td>
                          <Td><DeadlineCell o={o} today={today} /></Td>
                          <Td>
                            {o.delivery_kind === "carrier" ? <Tag>fora da área</Tag> : plan?.plan ? (
                              shell.role >= 5 ? (
                                <Button size="sm" variant="ghost" className="h-7 px-2" title="A proposta achou a transportadora mais barata; mover mostra o impacto antes de aplicar" onClick={() => setMoving(o)}>
                                  <ArrowRightLeft className="h-3.5 w-3.5" /> Mover para a frota…
                                </Button>
                              ) : <Tag tone="info">mais barata que a frota</Tag>
                            ) : <Tag tone="info">decide a proposta</Tag>}
                          </Td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
        <Note>Nas encomendas dentro da área da frota, a escolha entre frota e transportadora é da proposta, que compara pelo custo variável da rota.</Note>
      </Panel>
      {moving && plan?.plan && <MoveDialog shell={shell} order={moving} view={plan} vehicles={vehicles} onClose={() => setMoving(null)} onApplied={reloadPlan} />}
    </Page>
  )
}
